import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_LABEL_INDICATOR } from 'components/DashboardGrid/utils';
import { buildDuplicateAiBlockMarker } from 'ts-components/AiBlockPanel/aiBlockMarker';
import type { DashletLayout, DashletMutation } from './dashletTypes';
import {
  buildCustomCodeConfig,
  isCustomCodeContent,
  type CustomCodeContent,
} from './customCodeBlock';

type Dashlet = {
  id: string;
  name?: string;
  layout: DashletLayout;
  config?: Record<string, any>;
};

type Dashboard = {
  id: string;
};

type UseCustomCodeBlockParams = {
  currentDashboard?: Dashboard | null;
  dashlets: Dashlet[];
  updateDashletMutation: DashletMutation;
  duplicateDashlet: (args: {
    payload: unknown;
    oldDashlet?: Dashlet;
    dashlets: Dashlet[];
    dashboardId: string;
  }) => Promise<unknown> | void;
};

type MutateCustomCodeBlockArgs = {
  dashboardId?: string;
  content?: CustomCodeContent;
  dashletId?: string;
  isDuplicate?: boolean;
  name?: string;
};

export type CustomCodeEditorResult = {
  content: CustomCodeContent;
};

export const useCustomCodeBlock = ({
  currentDashboard,
  dashlets,
  updateDashletMutation,
  duplicateDashlet,
}: UseCustomCodeBlockParams) => {
  const [editingDashlet, setEditingDashlet] = useState<Dashlet | null>(null);

  const mutateCustomCodeBlock = useCallback(
    async ({
      dashboardId,
      content,
      dashletId,
      isDuplicate = false,
      name,
    }: MutateCustomCodeBlockArgs) => {
      if (!dashboardId) {
        return;
      }

      const existingDashlet = dashletId
        ? dashlets.find((d) => d.id === dashletId)
        : undefined;

      if (isDuplicate) {
        const existingContent = existingDashlet?.config?.content;
        if (!existingDashlet || !isCustomCodeContent(existingContent)) {
          return;
        }
        return duplicateDashlet({
          payload: {
            // A KiCode-built source makes the copy open KiCode as a fork of its conversation.
            config: buildCustomCodeConfig({
              content: existingContent,
              feExtraInfo: buildDuplicateAiBlockMarker(existingDashlet),
            }),
            layout: {
              i: uuidv4(),
              w: existingDashlet.layout.w,
              h: existingDashlet.layout.h,
            },
            name: name || DEFAULT_LABEL_INDICATOR,
          },
          oldDashlet: existingDashlet,
          dashlets,
          dashboardId,
        });
      }

      if (!content || !existingDashlet) {
        return;
      }

      return updateDashletMutation.mutateAsync({
        dashletId: existingDashlet.id,
        dashboardId,
        payload: {
          config: buildCustomCodeConfig({ content }),
          name: existingDashlet.name || DEFAULT_LABEL_INDICATOR,
          layout: {
            i: existingDashlet.layout.i,
            h: existingDashlet.layout.h,
            w: existingDashlet.layout.w,
            x: existingDashlet.layout.x,
            y: existingDashlet.layout.y,
          },
        },
      });
    },
    [dashlets, updateDashletMutation, duplicateDashlet]
  );

  const openCustomCodeEditor = useCallback((dashlet: Dashlet) => {
    setEditingDashlet(dashlet);
  }, []);

  const customCodeEditorProps = useMemo(() => {
    const initialContent = editingDashlet?.config?.content;
    if (!editingDashlet || !isCustomCodeContent(initialContent)) {
      return null;
    }
    return {
      initialContent,
      onHide: () => setEditingDashlet(null),
      onConfirm: ({ content }: CustomCodeEditorResult) => {
        setEditingDashlet(null);
        return mutateCustomCodeBlock({
          dashboardId: currentDashboard?.id,
          dashletId: editingDashlet.id,
          content,
        });
      },
    };
  }, [editingDashlet, currentDashboard, mutateCustomCodeBlock]);

  return { mutateCustomCodeBlock, openCustomCodeEditor, customCodeEditorProps };
};
